// Needle Drop hero (prototype).
// - prev/next between releases (manual only, no autoplay)
// - format stickers open the pressing sheet and redraw the disc in that pressing's colour
// - "Drop the needle" swings the arm on and hands the record to the Deck (assets/pr-deck.js)
// - scrolling the hero away rolls the disc into the Deck, which then shows the record cued up;
//   scrolling back returns it to the sleeve, unless the visitor has used the Deck
(() => {
  const root = document.querySelector('[data-pr-nd]');
  if (!root) return;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const slides = [...root.querySelectorAll('[data-pr-nd-slide]')];
  const counter = root.querySelector('[data-pr-nd-current]');
  let current = Math.max(0, slides.findIndex((s) => s.classList.contains('is-active')));

  const deck = () => window.PRDeck;
  const active = () => slides[current];
  const discOf = (slide) => slide.querySelector('[data-pr-nd-disc]');

  const trackOf = (slide) => ({
    kind: slide.dataset.kind,
    src: slide.dataset.src,
    sample: slide.dataset.sample === 'true',
    title: slide.dataset.title,
    artist: slide.dataset.artist,
    catalog: slide.dataset.catalog,
    url: slide.dataset.url,
    buy: slide.dataset.buy,
    disc: slide.querySelector('.pr-vinyl')?.outerHTML || '',
  });

  // Redraw the disc in a pressing colour (the colour table lives in assets/pr-deck.js)
  const restyle = (slide, name) => window.PRVinyl?.restyle(slide.querySelector('.pr-vinyl'), name);

  // ---- Slides ----
  const show = (index) => {
    const old = active();
    old.classList.remove('is-active', 'is-dropped');
    discOf(old).style.transform = '';
    current = (index + slides.length) % slides.length;
    active().classList.add('is-active');
    if (counter) counter.textContent = String(current + 1);
    syncDocked();
    frame();
  };
  root.querySelector('[data-pr-nd-prev]')?.addEventListener('click', () => show(current - 1));
  root.querySelector('[data-pr-nd-next]')?.addEventListener('click', () => show(current + 1));

  // A record that is on the Deck is not in its sleeve
  const syncDocked = () => {
    const onDeck = deck()?.current();
    slides.forEach((s) => {
      const docked = !!onDeck && onDeck.catalog === s.dataset.catalog && onDeck.cuedBy !== 'scroll';
      s.classList.toggle('is-docked', docked);
      if (!docked && !onDeck) s.classList.remove('is-dropped');
    });
  };
  document.addEventListener('pr:deck', () => {
    syncDocked();
    frame();
  });

  // ---- Stickers, sheets and the needle ----
  root.addEventListener('click', (event) => {
    const sticker = event.target.closest('[data-pr-nd-sticker]');
    if (sticker) {
      const slide = sticker.closest('[data-pr-nd-slide]');
      const opening = sticker.getAttribute('aria-expanded') !== 'true';
      slide.querySelectorAll('[data-pr-nd-sticker]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      slide.querySelectorAll('[data-pr-nd-sheet]').forEach((s) => (s.hidden = true));
      if (opening) {
        sticker.setAttribute('aria-expanded', 'true');
        const sheet = document.getElementById(sticker.getAttribute('aria-controls'));
        if (sheet) sheet.hidden = false;
      }
      if (sticker.dataset.vinyl) restyle(slide, sticker.dataset.vinyl);
      return;
    }

    const drop = event.target.closest('[data-pr-nd-drop]');
    if (!drop || !deck()) return;
    const slide = drop.closest('[data-pr-nd-slide]');
    slide.classList.add('is-dropped');
    const disc = discOf(slide);
    disc.style.transform = '';
    // Called inside the click so the browser lets the audio start
    deck().load(trackOf(slide), { play: true, from: disc, delay: reduce.matches ? 0 : 380 });
  });

  // ---- Scroll: roll the disc into the Deck ----
  const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  let ticking = false;

  const frame = () => {
    ticking = false;
    const d = deck();
    const slide = active();
    const disc = discOf(slide);
    if (!d || !disc) return;

    const rect = root.getBoundingClientRect();
    const p = Math.min(1, Math.max(0, -rect.top / (rect.height * 0.55)));
    const onDeck = d.current();

    // The visitor put something on the Deck themselves: leave the hero alone
    if (onDeck && onDeck.cuedBy !== 'scroll') {
      disc.style.transform = '';
      return;
    }

    if (p >= 1) {
      disc.style.transform = '';
      if (!onDeck) d.cue(trackOf(slide));
      slide.classList.add('is-docked');
      return;
    }

    if (onDeck) d.uncue();
    slide.classList.remove('is-docked');
    d.approach(p);

    if (reduce.matches || p === 0) {
      disc.style.transform = '';
      return;
    }

    disc.style.transform = '';
    const from = disc.getBoundingClientRect();
    const to = d.slotRect();
    if (!to || !to.width) return;
    const e = ease(p);
    const dx = (to.left + to.width / 2 - (from.left + from.width / 2)) * e;
    const dy = (to.top + to.height / 2 - (from.top + from.height / 2)) * e;
    const s = 1 + (to.width / from.width - 1) * e;
    disc.style.transform = `translate(${dx}px, ${dy}px) rotate(${-e * 420}deg) scale(${s})`;
  };

  const request = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(frame);
  };
  window.addEventListener('scroll', request, { passive: true });
  window.addEventListener('resize', request);

  // The Deck script may load after this one
  const start = () => {
    syncDocked();
    request();
  };
  if (window.PRDeck) start();
  else document.addEventListener('pr:deck-ready', start, { once: true });
})();
